<template>
  <div class="virement-section w-100">
    <!-- Header Section -->
    <div class="virement-header">
      <a-divider class="virement-divider">
        <span class="divider-text">{{
          $t("recette.saisirInfoVirement") ||
          "Saisir les informations de virement"
        }}</span>
      </a-divider>
    </div>

    <!-- Column Headers -->
    <div class="virement-columns-header">
      <div class="column-header index-col">Indice</div>
      <div class="column-header">Caisse bancaire</div>
      <div class="column-header">Numéro de virement</div>
      <div class="column-header">Titulaire / Nom</div>
      <div class="column-header">Montant</div>
      <div class="column-header">Pièce jointe</div>
      <div class="column-header actions-col">Actions</div>
    </div>

    <!-- Virement Items -->
    <div class="virement-items">
      <div
        v-for="(item, index) in virementsData"
        :key="index"
        class="virement-item"
        :class="{ 'virement-item--highlighted': index % 2 === 0 }"
      >
        <div class="virement-index">
          <span class="virement-number">{{ index + 1 }}</span>
        </div>

        <div class="virement-field">
          <a-select
            :loading="loadingCaisses"
            :value="item.caisseBank"
            placeholder="Sélectionner la banque"
            size="small"
            style="width: 100%"
            :class="{ 'input-error': isFieldInvalid(index, 'caisseBank') }"
            @change="
              (value) => handleVirementUpdate(index, 'caisseBank', value)
            "
          >
            <a-select-option
              v-for="caisse in caisseBanque"
              :key="caisse._id"
              :value="caisse._id"
            >
              {{ caisse.name + " (" + caisse.reference + ")" }}
            </a-select-option>
          </a-select>
        </div>

        <div class="virement-field">
          <a-input
            :value="item.num"
            placeholder="Numéro de virement"
            size="small"
            :class="{ 'input-error': isFieldInvalid(index, 'num') }"
            @input="(e) => handleNumInput(index, e.target.value)"
            @change="(e) => handleVirementUpdate(index, 'num', e.target.value)"
          />
        </div>

        <div class="virement-field">
          <a-input
            :value="item.titulaire"
            placeholder="Titulaire / Nom"
            size="small"
            :class="{ 'input-error': isFieldInvalid(index, 'titulaire') }"
            @change="
              (e) => handleVirementUpdate(index, 'titulaire', e.target.value)
            "
          />
        </div>

        <div class="virement-field">
          <a-input-number
            :value="item.montant"
            placeholder="Montant"
            size="small"
            style="width: 100%"
            :formatter="
              (value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            "
            :parser="(value) => value.replace(/\$\s?|(,*)/g, '')"
            :class="{ 'input-error': isFieldInvalid(index, 'montant') }"
            @change="(value) => handleVirementUpdate(index, 'montant', value)"
          />
        </div>

        <div class="virement-field">
          <a-upload
            :file-list="item.pieceJointe ? [item.pieceJointe] : []"
            :before-upload="(file) => handleBeforeUpload(index, file)"
            :custom-request="(file) => handleCustomRequest(file, index)"
            :remove="() => handleRemoveFile(index)"
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            :show-upload-list="{ showPreviewIcon: true, showRemoveIcon: true }"
          >
            <a-button size="small" icon="upload">
              {{ item.pieceJointe ? "Fichier joint" : "Joindre fichier" }}
            </a-button>
          </a-upload>
          <a-progress
            v-if="
              getProgress(index).value > 0 && getProgress(index).value < 100
            "
            :percent="getProgress(index).value"
            size="small"
            status="active"
          />
        </div>

        <div class="virement-actions">
          <a-button
            v-if="index > 0"
            type="text"
            size="small"
            danger
            @click="handleRemoveVirement(index)"
            class="remove-btn"
            icon="delete"
            title="Supprimer ce virement"
          >
          </a-button>
        </div>
      </div>
    </div>

    <!-- Add Virement Button -->
    <div class="virement-actions-footer">
      <a-button
        type="dashed"
        icon="plus"
        @click="handleAddVirement"
        class="add-virement-btn"
        size="large"
      >
        Ajouter un virement
      </a-button>
    </div>
  </div>
</template>

<script>
import { uploadFilesToS3 } from "@/services/uploadService";
import apiClient from "@/services/axios";

export default {
  name: "VirementSection",
  props: {
    virementsData: {
      required: true,
      type: Array,
    },
    virementVide: {
      required: true,
      type: Object,
    },
  },

  data() {
    return {
      progress: {},
      caisseBanque: [],
      loadingCaisses: true,
      requiredFields: ["caisseBank", "num", "titulaire", "montant"],
      validationErrors: [],
    };
  },

  computed: {
    getProgress() {
      return (index) => {
        return this.progress[index] || { value: 0 };
      };
    },
  },

  created() {
    // Initialize progress for existing virements
    this.fetchCaisses();
    this.virementsData.forEach((_, index) => {
      this.$set(this.progress, index, { value: 0 });
    });
  },

  methods: {
    handleVirementUpdate(index, field, value) {
      this.$emit("update-virement", index, field, value);
      if (field === "montant") {
        this.$emit("calculate-montant");
      }
      this.validateVirement(index);
    },

    handleAddVirement() {
      // Validate last virement before adding
      const lastIndex = this.virementsData.length - 1;
      if (!this.validateVirement(lastIndex)) {
        return;
      }
      this.$emit("add-virement");
      // Initialize progress for the new virement
      const newIndex = this.virementsData.length;
      this.$set(this.progress, newIndex, { value: 0 });
    },

    handleRemoveVirement(index) {
      this.$emit("remove-virement", index);
      // Clean up progress for removed virement
      this.$delete(this.progress, index);
      this.validationErrors.splice(index, 1);
    },
    handleCustomRequest(file, index) {
      // Initialize progress for this index if not exists
      if (!this.progress[index]) {
        this.$set(this.progress, index, { value: 0 });
      }
      uploadFilesToS3([file.file], "finance/virements", this.getProgress(index))
        .then((keys) => {
          // Update the virement data with the uploaded file info
          this.$emit("update-virement", index, "file", keys[0]);

          // Reset progress
          this.$set(this.progress, index, { value: 0 });

          this.$message.success("Fichier téléchargé avec succès");
        })
        .catch((error) => {
          console.error("Upload failed:", error);
          this.$message.error("Erreur lors du téléchargement du fichier");

          // Reset progress on error
          this.$set(this.progress, index, { value: 0 });
        });
    },

    handleBeforeUpload(index, file) {
      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/jpg",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      const isValidType = allowedTypes.includes(file.type);
      if (!isValidType) {
        this.$message.error(
          "Type de fichier non supporté. Veuillez sélectionner un fichier PDF, image ou document."
        );
        return false;
      }

      // Validate file size (5MB limit)
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        this.$message.error(
          "Le fichier est trop volumineux. La taille maximale est de 5MB."
        );
        return false;
      }
    },

    handleRemoveFile(index) {
      this.$emit("update-virement", index, "file", null);
    },
    async fetchCaisses() {
      try {
        this.loadingCaisses = true;
        const res = await apiClient.get("/compV3/caisse/bank");
        this.caisseBanque = res.data;
        this.loadingCaisses = false;
      } catch (error) {
        console.log(error);
        this.$message.error("Erreur lors du chargement des comptes bancaires");
        this.loadingCaisses = false;
      }
    },

    validateVirement(index) {
      const virement = this.virementsData[index];
      if (!virement) return true;
      const errors = {};
      this.requiredFields.forEach((field) => {
        if (field === "montant") {
          if (!virement[field] || virement[field] <= 0) {
            errors[field] = true;
          }
        } else if (field === "num") {
          if (!virement[field] || virement[field].length === 0) {
            errors[field] = true;
          } else if (!/^[0-9]+$/.test(virement[field])) {
            errors[field] = true;
          }
        } else {
          if (!virement[field]) {
            errors[field] = true;
          }
        }
      });
      this.$set(this.validationErrors, index, errors);
      return Object.keys(errors).length === 0;
    },
    isFieldInvalid(index, field) {
      return (
        this.validationErrors[index] && this.validationErrors[index][field]
      );
    },
    handleNumInput(index, value) {
      // Allow only numbers
      this.$emit("update-virement", index, "num", value.replace(/[^0-9]/g, ""));
    },
  },
};
</script>

<style scoped>
.virement-section {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.virement-header {
  padding: 16px 24px 0;
}

.virement-divider {
  margin: 0;
}

.divider-text {
  font-weight: 600;
  color: #1890ff;
  font-size: 16px;
}

.virement-columns-header {
  display: grid;
  grid-template-columns: 60px 1fr 1fr 1fr 1fr 1fr 80px;
  gap: 12px;
  padding: 16px 24px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  font-weight: 600;
  color: #262626;
  font-size: 14px;
}

.column-header {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.column-header:first-child,
.column-header:last-child {
  justify-content: center;
}

.index-col {
  justify-content: center;
}

.actions-col {
  justify-content: center;
}

.virement-items {
  max-height: 400px;
  overflow-y: auto;
}

.virement-item {
  display: grid;
  grid-template-columns: 60px 1fr 1fr 1fr 1fr 1fr 80px;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.3s ease;
}

.virement-item:hover {
  background: #f6ffed;
}

.virement-item:last-child {
  border-bottom: none;
}

.virement-item--highlighted {
  background: #fafafa;
}

.virement-item--highlighted:hover {
  background: #f6ffed;
}

.virement-index {
  display: flex;
  align-items: center;
  justify-content: center;
}

.virement-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #1890ff;
  color: white;
  border-radius: 50%;
  font-weight: 600;
  font-size: 14px;
}

.virement-field {
  display: flex;
  align-items: center;
}

.virement-actions {
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-btn {
  color: #ff4d4f;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.remove-btn:hover {
  background: #fff2f0;
  color: #ff7875;
}

.virement-actions-footer {
  padding: 24px;
  text-align: center;
  border-top: 1px solid #f0f0f0;
}

.add-virement-btn {
  height: 48px;
  padding: 0 32px;
  font-size: 16px;
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.add-virement-btn:hover {
  border-color: #1890ff;
  color: #1890ff;
}

/* File upload styles */
.ant-upload {
  width: 100%;
}

.ant-upload-list {
  margin-top: 8px;
}

.ant-upload-list-item {
  padding: 4px 8px;
  border-radius: 4px;
  background: #f5f5f5;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .virement-columns-header,
  .virement-item {
    grid-template-columns: 50px 1fr 1fr 1fr 1fr 1fr 60px;
    gap: 8px;
    padding: 12px 16px;
  }

  .virement-columns-header {
    padding: 12px 16px;
    font-size: 12px;
  }
}

@media (max-width: 768px) {
  .virement-columns-header,
  .virement-item {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 12px;
  }

  .column-header {
    justify-content: flex-start;
    text-align: left;
  }

  .virement-field {
    flex-direction: column;
    align-items: flex-start;
  }

  .virement-actions {
    justify-content: flex-start;
  }

  .virement-index {
    justify-content: flex-start;
  }

  .virement-header {
    padding: 12px 16px 0;
  }

  .virement-actions-footer {
    padding: 12px 16px;
  }

  .divider-text {
    font-size: 14px;
  }
}

/* Scrollbar Styling */
.virement-items::-webkit-scrollbar {
  width: 6px;
}

.virement-items::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.virement-items::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.virement-items::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.input-error {
  border: 1px solid #ff4d4f !important;
  box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
}
</style>
